import { Suspense, lazy, memo, useMemo } from "react";
import { useLocation } from "react-router-dom";
import LoadingBoundary from "../loading/LoadingBoundary";
import UnAuthenticated from "../templates/UnAuthenticated";
import { getAppIndexComponent } from "../utils/getAppIndexComponent";
import { isDesignRenderRoute } from "../utils/isDesignRenderRoute";

const DesignerRender = lazy(() => import("../features/designer/Index"));

interface AppContentProps {
  configData: any;
  params: any;
  isLoading: boolean;
  isDataLoading: boolean;
}

export const AppContent = memo(
  ({ configData, params, isLoading, isDataLoading }: AppContentProps) => {
    const location = useLocation();
    const isGalleryShell = location.pathname.endsWith("gallery.html");
    const isDesignerRender = isDesignRenderRoute(location.pathname);

    // Memoize the app key to prevent unnecessary re-computations
    const appKey = useMemo(
      () => configData?.appKey! || configData?.app?.key,
      [configData?.appKey, configData?.app?.key]
    );

    const galleryLoadingFallback = useMemo(() => {
      if (!isGalleryShell) {
        return null;
      }

      return (
        <div className="w-full h-screen flex flex-col justify-center items-center gap-3 pointer-events-none bg-transparent">
          <div
            className="h-[64px] w-[64px] rounded-full border-[6px] border-solid border-[#d9d9d9] border-t-[#222] animate-spin"
            aria-hidden="true"
          />
          <p className="text-main-100">Loading gallery...</p>
        </div>
      );
    }, [isGalleryShell]);

    // Memoize the Index component to prevent unnecessary re-imports
    const Index = useMemo(() => getAppIndexComponent(appKey), [appKey]);

    // Memoize validation checks
    const validationState = useMemo(() => {
      const isIndexInvalid = Index === null;
      const isConfigDataInvalid = !configData?.configs;
      const isCustomDataEmpty =
        configData?.app?.type === "custom_embed" &&
        Object.keys(configData?.data ?? {}).length === 0 &&
        Object.keys(configData?.configs ?? {}).length === 0;

      return {
        isIndexInvalid,
        isConfigDataInvalid,
        isCustomDataEmpty,
        shouldShowUnauthenticated:
          isIndexInvalid || isConfigDataInvalid || isCustomDataEmpty,
      };
    }, [Index, configData?.configs, configData?.app?.type, configData?.data]);

    const isPreviewMode = location.search.includes("?configs=");
    const shouldShowLoading = isPreviewMode
      ? isLoading || !configData
      : isDataLoading || !configData;

    if (isDesignerRender) {
      return (
        <Suspense fallback={null}>
          <DesignerRender />
        </Suspense>
      );
    }

    if (shouldShowLoading) {
      if (isPreviewMode) {
        if (galleryLoadingFallback) {
          return galleryLoadingFallback;
        }

        return (
          <LoadingBoundary
            loading={true}
            className="w-full h-screen flex justify-center items-center pointer-events-none"
          />
        );
      } else {
        return (
          galleryLoadingFallback || <div className="w-full h-screen bg-transparent" />
        );
      }
    }

    if (validationState.shouldShowUnauthenticated) {
      return (
        <UnAuthenticated isPreview={!location.search.includes("?configs=")} />
      );
    }

    // Render the main app component
    return (
      <>
        {Index && (
          <LoadingBoundary
            loading={isPreviewMode ? false : isLoading}
            className="w-full h-screen flex justify-center items-center pointer-events-none"
          >
            <Suspense
              fallback={
                galleryLoadingFallback || (
                  <div className="w-full h-screen flex justify-center items-center pointer-events-none"></div>
                )
              }
            >
              <Index
                key={
                  configData?.data ? JSON.stringify(configData.data) : "no-data"
                }
                configsFromLayout={configData}
                params={params}
              />
            </Suspense>
          </LoadingBoundary>
        )}
      </>
    );
  },
  // Custom comparison function to ensure re-renders when data changes
  (prevProps, nextProps) => {
    // Always re-render if configData changes (deep comparison for data)
    if (prevProps.configData?.data !== nextProps.configData?.data) {
      return false; // Re-render
    }

    // Re-render if other important props change
    if (
      prevProps.isLoading !== nextProps.isLoading ||
      prevProps.isDataLoading !== nextProps.isDataLoading ||
      prevProps.configData?.app?.auto_reload !==
        nextProps.configData?.app?.auto_reload
    ) {
      return false; // Re-render
    }

    return true; // Don't re-render
  }
);
